import { useState, useEffect, useContext } from "react";
import MainLayouts from "../../Layout/MainLayouts";
import { ReactComponent as PlusSquareFill } from "bootstrap-icons/icons/plus-square-fill.svg";
import { ReactComponent as PencilSquare } from "bootstrap-icons/icons/pencil-square.svg";
import { ReactComponent as Trash } from "bootstrap-icons/icons/trash3.svg";
import { ReactComponent as Eye } from "bootstrap-icons/icons/eye.svg";
import { ReactComponent as ChevronRightIcon } from "bootstrap-icons/icons/chevron-right.svg";
import { ReactComponent as XCircleFill } from "bootstrap-icons/icons/x-circle-fill.svg";
import Spinner from "../spinner/Spinner";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import ApiServices from "../../api/ApiServices";
import { toast } from "react-toastify";
import DataTable from "react-data-table-component";
import Swal from "sweetalert2";
import CustomStyles from "../../components/utils/CustomStyles";
import { handleError } from "../../components/utils/AuthenticationCheck";
import formatDate from "../../components/utils/DateFormatter";
import CurrencyFormatter from "../../components/utils/CurrencyFormatter";
import { AuthContext } from "../../AuthProvider/AuthProvider";
import AxiosApiCall from "../../api/AxiosApiCall";

const GetAllOrders = () => {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(false);
    const [perPage, setPerPage] = useState(100);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [filterText, setFilterText] = useState("");
    const [resetPaginationToggle, setResetPaginationToggle] = useState(false);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [selectedOrderType, setSelectedOrderType] = useState("");
    const { permissions } = useContext(AuthContext)

    const navigate = useNavigate();
    //axios api encryption call
    const axios = AxiosApiCall();


    // Function to format permission text
    const formatPermissionText = (text) => {
        // Replace underscores with spaces
        let formattedText = text.replace(/_/g, " ");

        // Capitalize the first letter of each word
        formattedText = formattedText
            .toLowerCase()
            .replace(/\b\w/g, (char) => char.toUpperCase());

        return formattedText;
    };


    const fetchAllOrdersList = async (page = 1, perPage = 100) => {
        setLoading(true);
        try {
            const authData = JSON.parse(localStorage.getItem("auth"));
            const token = authData?.token;

            if (!token) {
                throw new Error("No access token found.");
            }

            const response = await axios.get(ApiServices.getAllOrders, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
                params: {
                    page,
                    per_page: perPage
                },
                withCredentials: true,
            });

            const { status, message, data } = response.data;

            if (status === 200 && Array.isArray(data.orders)) {
                setOrders(data.orders);
                setPerPage(data.per_page);
                setTotal(data.total);
                setPage(data.current_page);
                toast.success(message, { toastId: "orders-fetch-success" });
            } else {
                handleError({ response: { status, data: { message } } }, navigate);
            }
        } catch (error) {
            handleError(error, navigate);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAllOrdersList(page, perPage);
    }, [page, perPage]);

    // Handle page change - reset to page 1 when filters change
    const handlePageChange = (newPage) => {
        setPage(newPage);
        // console.log("Page changed to:", newPage);
    };

    // Handle rows per page change
    const handlePerRowsChange = (newPerPage, newPage) => {
        setPerPage(newPerPage);
        setPage(newPage); // Reset to first page
        // console.log("Rows per page changed:", newPerPage, "to page:", newPage);
    };


    // admin cancel customer order api cal here
    const submitCancellation = async (orderId, userId, reason) => {
        const formData = {
            order_id: orderId,
            reason: reason,
            user_id: userId,
        }
        // console.log("form data for submission", formData);
        try {
            const authData = JSON.parse(localStorage.getItem("auth"));
            const token = authData?.token;
            if (!token) {
                throw new Error("No access token found.");
            }

            const response = await axios.post(
                ApiServices.returnCustomerOrderByAdmin,
                {
                    order_id: orderId,
                    user_id: userId,
                    reason: reason
                },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                    withCredentials: true,
                }
            );

            const { status, message } = response.data;
            if (status === 200) {
                toast.success(message);
                // Refresh orders or update local state
                // const updatedOrders = orders.map(order =>
                //     order.id === orderId ? { ...order, status: 'cancelled' } : order
                // );
                // setOrders(updatedOrders);
                fetchAllOrdersList();
            } else {
                toast.error(message || 'Failed to cancel order');
            }
        } catch (error) {
            handleError(error, navigate);
            toast.error('An error occurred while cancelling the order');
        }
    };

    // Handle confirmation pop-up for product update
    const handleCancelOrder = (orderId, userId) => {
        Swal.fire({
            title: 'Cancel Order',
            html: `
            <p>Are you sure you want to cancel this order?</p>
            <textarea 
                id="reason" 
                class="swal2-textarea" 
                placeholder="Enter cancellation reason..."
                required
            ></textarea>
        `,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Confirm Cancellation',
            cancelButtonText: 'Cancel',
            focusConfirm: false,
            preConfirm: () => {
                const reason = Swal.getPopup().querySelector('#reason').value;
                if (!reason) {
                    Swal.showValidationMessage('Please enter a cancellation reason');
                    return false;
                }
                return { reason };
            }
        }).then((result) => {
            if (result.isConfirmed) {
                const { reason } = result.value;
                submitCancellation(orderId, userId, reason);
            }
        });
    };

    const handleFilter = (e) => {
        setFilterText(e.target.value);
        setResetPaginationToggle(!resetPaginationToggle);
    };


    // Define Expandable Component for Actions
    const ExpandableComponent = ({ data }) => (
        <div className="flex justify-start  relative">
            <div className="mx-2 my-2 group relative">
                {permissions.some(permission => permission.name === 'order.view') && (
                    <Link
                        to="/admin/order/details"

                        // state={{ orders: row }}
                        state={{ orderId: data.id }}
                        className="mx-2 group"
                    >
                        <Eye className="w-5 h-5 text-darkGreen3" />
                        <div className="absolute top-full left-2/2 transform -translate-x-1/2 mb-1 hidden group-hover:block text-dash5 text-xs whitespace-nowrap">
                            Details
                        </div>
                    </Link>
                )}
            </div>
            <div className="mx-2 my-2 group relative">
                {permissions.some(permission => permission.name === 'order.edit') && (
                    <Link
                        to="/admin/order/update"
                        state={{ orders: data }}
                        // state={{ orderId: row.id }}
                        className="mx-2  group"
                    >
                        <PencilSquare className="w-5 h-5 text-dash4" />
                        <div className="absolute top-full left-2/2 transform -translate-x-1/2 mb-1 hidden group-hover:block text-dash5 text-xs whitespace-nowrap">
                            Edit
                        </div>
                    </Link>
                )}
            </div>
            {/* <div className="mx-2 my-2 group relative">
                {permissions.some(permission => permission.name === 'order.return') && (
                    <Link
                        to="/admin/returned/customer/orders"
                        state={{ orders: data }}
                        // state={{ orderId: row.id }}
                        className="mx-2  group"
                    >
                        <XCircleFill className="w-5 h-5 text-dash3" />
                        <div className="absolute top-full left-2/2 transform -translate-x-1/2 mb-1 hidden group-hover:block text-dash5 text-xs whitespace-nowrap">
                            Cancel order
                        </div>
                    </Link>
                )}
            </div> */}
            {/* {permissions.some(permission => permission.name === 'order.return') && (
                <button
                    onClick={() => handleCancelOrder(data.id, data.user_id)}
                    className="mx-2 group"
                >
                    <XCircleFill className="w-5 h-5 text-dash3" />
                    <div className="absolute top-full left-2/2 transform -translate-x-1/2 mb-1 hidden group-hover:block text-dash5 text-xs whitespace-nowrap">
                        Cancel order
                    </div>
                </button>
            )} */}
            {/* <Link
                        className="mx-2 my-2 group"
                        disabled={confirmationLoading}
                    >
                        <Trash className="w-5 h-5 text-dash3" />
                        <div className="absolute top-full left-2/2 transform -translate-x-1/2 mb-1 hidden group-hover:block text-dash5 text-xs whitespace-nowrap">
                            Delete orders
                        </div>
                    </Link> */}
        </div>
    );


    const columns = [

        {
            name: "Customer Name",
            selector: (row) => row.user_name.toUpperCase() || "NOT AVAILABLE",
            sortable: true,
            width: "200px",
        },
        {
            name: "Tracking Id",
            selector: (row) => row.tracking_id ? row.tracking_id : "NOT AVIALABLE",
            sortable: true,
            width: "200px",
        },
        {
            name: "Total Amount",
            selector: (row) => CurrencyFormatter(row.total_amount) || "NOT AVAILABLE",
            sortable: true,
            width: "200px",
        },
        {
            name: "Type",
            selector: (row) => formatPermissionText(row.order_type).toUpperCase() || "NOT AVAILABLE",
            sortable: false,
            width: "200px",
        },
        {
            name: "Status",
            selector: (row) => row.status ? row.status.toUpperCase() : "NOT AVAILABLE",
            sortable: true,
            cell: (row) => {
                let bgColor = "#A0AEC0"; // Default background color
                let textColor = "#F7F7F7"; // Default text color

                if (row.status === "processing") {
                    bgColor = "#003FB9"; // Color for processing
                } else if (row.status === "cancelled") {
                    bgColor = "#CC0000"; // Color for cancelled
                } else if (row.status === "completed") {
                    bgColor = "#FFA439"; // Color for completed
                } else if (row.status === "failed") {
                    bgColor = "#CC0000"; // Color for completed
                } else if (row.status === "shipped") {
                    bgColor = "#48BB78";
                } else if (row.status === "ongoing") {
                    bgColor = "#1E4C2F";
                }

                return (
                    <span style={{ backgroundColor: bgColor, color: textColor, padding: '5px 10px', borderRadius: '4px' }}>
                        {row.status ? row.status.toUpperCase() : "NOT AVAILABLE"}
                    </span>
                );
            },
            width: "150px",
        },

        // {
        //     name: "Order Items",
        //     selector: (row) =>
        //         row.order_items.map(item => item.stock).join(", ") || "NOT AVAILABLE",
        //     sortable: false,
        //     width: "300px",
        // },
        // {
        //     name: "Order Items",
        //     selector: (row) => {
        //         const items = [
        //             ...(row.order_items || []).map(i => i.stock),
        //             ...(row.physical_items || []).map(i => i.network_name)
        //         ].filter(Boolean);

        //         return items.length > 0
        //             ? items.join(", ")
        //             : "NOT AVAILABLE";
        //     },
        // },
        {
            name: "Order Items",
            selector: (row) => {
                // Debugging: Log the actual data structure
                // console.log('Order items:', row.order_items);
                // console.log('Physical items:', row.physical_items);
                // Check physical items first since that's your primary case
                const physicalItems = row.physical_items || [];
                if (physicalItems.length > 0) {
                    return physicalItems.map(i => i.network_name.toUpperCase() || "NO NETWORK").join(", ");
                }

                // Fallback to order items
                const orderItems = row.order_items || [];
                if (orderItems.length > 0) {
                    return orderItems.map(i => i.stock.toUpperCase() || "NO STOCK").join(", ");
                }

                return "NOT AVAILABLE";
            },
        },

        {
            name: "Created At",
            selector: (row) => formatDate(row.created_at) || "NOT AVAILABLE",
            sortable: true,
            width: "250px",
        },
    ];

    // //selct user type
    const handleOrderTypeSelect = (type) => {
        setSelectedOrderType(type);
        setIsDropdownOpen(false);
    };


    // const applyFilters = (orders) => {
    //     const lowerFilterText = filterText.toLowerCase();

    //     return orders.filter(order => {
    //         const matchesUserName = order.user_name?.toLowerCase().includes(lowerFilterText);
    //         const matchesProductOrderType = order.order_type?.toLowerCase().includes(lowerFilterText);
    //         const matchesTotalAmount = order.total_amount?.toString().includes(lowerFilterText);
    //         const matchesStatus = order.status?.toString().includes(lowerFilterText);
    //         const matchesOrderItemsTrackingId = order.tracking_id?.includes(lowerFilterText);
    //         const matchesOrderItems = order.order_items.some(item =>
    //             item.stock?.toLowerCase().includes(lowerFilterText)
    //         );
    //         const matchesOrderType = !selectedOrderType || order.status.toLowerCase() === selectedOrderType.toLowerCase();

    //         return (matchesUserName || matchesProductOrderType || matchesTotalAmount || matchesOrderItems || matchesOrderItemsTrackingId || matchesStatus) && matchesOrderType;
    //     });
    // };

    const applyFilters = (orders) => {
        const lowerFilterText = filterText.toLowerCase();

        return orders.filter(order => {
            const matchesUserName = order.user_name?.toLowerCase().includes(lowerFilterText);
            const matchesCreatedAt = order.created_at?.toLowerCase().includes(lowerFilterText);
            const matchesProductOrderType = order.order_type?.toLowerCase().includes(lowerFilterText);
            const matchesTotalAmount = order.total_amount?.toString().includes(lowerFilterText);
            const matchesStatus = order.status?.toString().toLowerCase().includes(lowerFilterText);
            const matchesOrderItemsTrackingId = order.tracking_id?.toString().toLowerCase().includes(lowerFilterText);
            const matchesOrderItems = order.order_items?.some(item =>
                item.stock?.toLowerCase().includes(lowerFilterText)
            );
            const matchesOrderType = !selectedOrderType || order.status?.toLowerCase() === selectedOrderType.toLowerCase();

            return (
                (matchesUserName ||
                    matchesCreatedAt ||
                    matchesProductOrderType ||
                    matchesTotalAmount ||
                    matchesOrderItems ||
                    matchesOrderItemsTrackingId ||
                    matchesStatus) &&
                matchesOrderType
            );
        });
    };


    // In your render logic:
    const filteredItems = applyFilters(orders);
    const data = filteredItems.map((order) => ({
        user_name: order.user_name || "NOT AVAILABLE",
        total_amount: order.total_amount || "NOT AVAILABLE",
        status: order.status || "NOT AVAILABLE",
        created_at: order.created_at || "NOT AVAILABLE",
        order_items: order.order_items ? order.order_items.map(item => ({
            stock: item.stock || "NOT AVAILABLE",
            quantity: item.quantity || 0,
            price: item.price || "0.00",
        })) : [],
        physical_items: order.physical_items ? order.physical_items.map(item => ({
            network_name: item.network_name || "NOT AVAILABLE",
            quantity: item.quantity || 0,
            price: item.price || "0.00",
        })) : [],
        id: order.id,
        user_id: order.user_id,
        order_type: order.order_type,
        tracking_id: order.tracking_id,
    }));

    // Sort data based on creation date
    const sortedData = [...data].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

    return (
        <div>
            <MainLayouts>
                <div className=" sm:rounded-lg py-6 w-full">
                    <section
                        className="w-full pr-[3%] pt-3 my-3"
                        style={{ backgroundColor: "#F7F7F7" }}
                    >
                        <nav className="bg-gray-200 py-0 px-4 rounded-lg">
                            <ol className="list-none p-0 inline-flex mb-2">
                                <li className="flex items-center">
                                    <p className="text-blue-500 hover:text-blue-600 text-darkGreen3 pr-2">
                                        Orders
                                    </p>
                                    <ChevronRightIcon />
                                </li>
                                <li className="flex items-center">
                                    <p className="text-orange1">List</p>
                                </li>
                            </ol>
                        </nav>
                    </section>
                    <div className="pb-4 bg-white dark:bg-gray-900 flex flex-wrap justify-between">
                        <div className="relative w-full sm:w-1/2 mb-4 sm:mb-0">
                            <input
                                type="search"
                                className="block w-full px-3 py-2 text-md text-gray-900 border border-gray-300 rounded-lg bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white"
                                placeholder="Search"
                                value={filterText}
                                onChange={handleFilter}
                            />
                        </div>
                        {permissions.some(permission => permission.name === 'order.view') && (
                            <div className="relative w-full md:w-auto flex-shrink-0">
                                <div>
                                    <button
                                        type="button"
                                        className="bg-darkGreen3 inline-flex w-full justify-center gap-x-1.5 rounded-md px-3 py-3 text-sm font-semibold text-dGrey3"
                                        id="menu-button"
                                        aria-expanded={isDropdownOpen}
                                        aria-haspopup="true"
                                        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                                    >
                                        Select Order Status
                                        <svg
                                            className="-mr-1 h-5 w-5 text-gray-400"
                                            viewBox="0 0 20 20"
                                            fill="currentColor"
                                            aria-hidden="true"
                                        >
                                            <path
                                                fillRule="evenodd"
                                                d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
                                                clipRule="evenodd"
                                            />
                                        </svg>
                                    </button>
                                </div>
                                {isDropdownOpen && (
                                    <div
                                        className="absolute right-0 z-10 mt-2 w-full md:w-56 origin-top-right divide-y divide-gray-100 rounded-md bg-lGrey shadow-lg ring-1 ring-black1 ring-opacity-5 focus:outline-none"
                                        role="menu"
                                        aria-orientation="vertical"
                                        aria-labelledby="menu-button"
                                    >

                                        <div
                                            className="py-1 rounded-md"
                                            role="none"
                                            style={{ backgroundColor: "white" }}
                                        >
                                            <button
                                                onClick={() => handleOrderTypeSelect("pending")}
                                                className="block w-full px-4 py-2 text-sm text-gray-700 hover:bg-white hover:text-darkGreen3"
                                                role="menuitem"
                                            >
                                                Pending
                                            </button>

                                            <button
                                                onClick={() => handleOrderTypeSelect("ongoing")}
                                                className="block w-full px-4 py-2 text-sm text-gray-700 hover:bg-white hover:text-darkGreen3"
                                                role="menuitem"
                                            >
                                                Ongoing
                                            </button>
                                            <button
                                                onClick={() => handleOrderTypeSelect("completed")}
                                                className="block w-full px-4 py-2 text-sm text-gray-700 hover:bg-white hover:text-darkGreen3"
                                                role="menuitem"
                                            >
                                                Completed
                                            </button>

                                            <button
                                                onClick={() => handleOrderTypeSelect("shipped")}
                                                className="block w-full px-4 py-2 text-sm text-gray-700 hover:bg-white hover:text-darkGreen3"
                                                role="menuitem"
                                            >
                                                Shipped
                                            </button>
                                            <button
                                                onClick={() => handleOrderTypeSelect("cancelled")}
                                                className="block w-full px-4 py-2 text-sm text-gray-700 hover:bg-white hover:text-darkGreen3"
                                                role="menuitem"
                                            >
                                                Cancelled
                                            </button>
                                            <button
                                                onClick={() => handleOrderTypeSelect("failed")}
                                                className="block w-full px-4 py-2 text-sm text-gray-700 hover:bg-white hover:text-darkGreen3"
                                                role="menuitem"
                                            >
                                                Failed
                                            </button>
                                            <button
                                                onClick={() => handleOrderTypeSelect("processing")}
                                                className="block w-full px-4 py-2 text-sm text-gray-700 hover:bg-white hover:text-darkGreen3"
                                                role="menuitem"
                                            >
                                                Processing
                                            </button>

                                            <div className="relative w-full md:w-auto flex-shrink-0">
                                                <button
                                                    type="button"
                                                    className="block w-full px-4 py-2 text-sm text-gray-700 hover:bg-white hover:text-darkGreen3"
                                                    onClick={() => handleOrderTypeSelect("")} // Reset filter
                                                >
                                                    All Orders
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                    {loading ? (
                        <Spinner loading={loading} />
                    ) : (

                        <DataTable
                            columns={columns}
                            data={sortedData}
                            pagination
                            paginationServer
                            paginationRowsPerPageOptions={[100]}
                            paginationTotalRows={total}
                            paginationDefaultPage={page}
                            paginationPerPage={perPage}
                            onChangePage={handlePageChange}
                            onChangeRowsPerPage={handlePerRowsChange}
                            progressPending={loading}
                            progressComponent={<Spinner loading={loading} />}
                            highlightOnHover
                            noDataComponent={<div>No order found</div>}
                            expandableRows
                            expandableRowsComponent={ExpandableComponent}
                            customStyles={CustomStyles}
                        />
                    )}
                </div>
            </MainLayouts>
        </div>
    );
};

export default GetAllOrders;
